import React from "react";
import { SemoIcon as SemoIcon } from "@semo/icons";
import { Button } from "../primitives/button";
import { Avatar, AvatarFallback, AvatarImage } from "../primitives/avatar";
import { cn } from "../utils/cn";

export interface SidebarInfoCardProps {
  title: string;
  name: string;
  subText?: string;
  avatarIcon?: SemoIcon;
  imageUrl?: string;
  infoRows: {
    icon: SemoIcon;
    value: string;
  }[];
  actionButton?: {
    label: string;
    icon?: SemoIcon;
    onClick: () => void;
    variant?:
      | "default"
      | "outline"
      | "ghost"
      | "secondary"
      | "destructive"
      | "outline-primary";
    disabled?: boolean;
  };
  className?: string;
}

export const SidebarInfoCard: React.FC<SidebarInfoCardProps> = ({
  title,
  name,
  subText,
  avatarIcon: AvatarIcon,
  imageUrl,
  infoRows,
  actionButton,
  className,
}) => {
  return (
    <div
      className={cn(
        "bg-white rounded-[20px] border border-gray-100 shadow-sm p-6 h-fit sticky top-6 transition-all hover:shadow-md",
        className,
      )}
    >
      {title && (
        <h3 className="text-2xs font-semibold text-gray-400 uppercase tracking-wider mb-5">
          {title}
        </h3>
      )}

      <div className="flex items-start gap-4 mb-6">
        <Avatar className="w-16 h-16 rounded-full border-2 border-primary/10 shadow-sm overflow-hidden">
          {imageUrl ? (
            <AvatarImage src={imageUrl} alt={name} className="object-cover" />
          ) : null}
          <AvatarFallback className="bg-gray-50 text-primary">
            {AvatarIcon ? (
              <AvatarIcon className="w-8 h-8 opacity-40" />
            ) : (
              <span className="text-xl font-semibold">{name[0]}</span>
            )}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0 flex flex-col justify-center h-16">
          <div className="text-lg font-semibold text-gray-900 truncate tracking-tight leading-tight">
            {name}
          </div>
          {subText && (
            <div className="text-xs text-gray-400 font-semibold mt-1 tracking-tight">
              {subText}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3 mb-6">
        {infoRows.map((row, index) => (
          <div
            key={index}
            className="flex items-center gap-3 text-sm bg-gray-50/50 p-3 rounded-[12px] border border-gray-50 transition-colors hover:border-primary/10"
          >
            <row.icon className="w-4 h-4 text-primary/60 shrink-0" />
            <span className="text-gray-600 truncate font-semibold tracking-tight">
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {actionButton && (
        <Button
          className="w-full text-sm font-semibold py-3 h-auto rounded-[12px] shadow-sm hover:shadow-md transition-all"
          onClick={actionButton.onClick}
          variant={actionButton.variant ?? "outline-primary"}
          disabled={actionButton.disabled}
        >
          {actionButton.icon && <actionButton.icon className="w-4 h-4 mr-2" />}
          {actionButton.label}
        </Button>
      )}
    </div>
  );
};
